<template>
    <SearchForm @initForm="initForm" @submit="searchHandler">
        <div class="form-input-group">
            <p class="title date">날짜/기간</p>
            <div class="form-input-select">
                <BaseSelect
                    :option-list="PERIOD_TYPE_OPTION_LIST"
                    v-model="periodType"
                />
                <DateRangePicker
                    v-if="formState.fromDate && formState.toDate"
                    style="width: 280px"
                    v-model:fromDate="formState.fromDate"
                    v-model:toDate="formState.toDate"
                    :maxRange="{type: 'months', value: 6}"
                    :shortcuts="shortcuts"
                    @update:from-date="updateFromDateValue"
                    @update:to-date="updateToDateValue"
                />
            </div>
        </div>

        <div class="form-input-group">
            <p class="title type">지급/회수 형태</p>
            <div class="form-input-select">
                <BaseSelect
                    :option-list="SERVICE_TYPE_OPTION_LIST"
                    v-model="provideType"
                />
            </div>
        </div>

        <div class="form-input-group">
            <p class="title status">처리상태</p>
            <div class="form-input-select">
                <BaseSelect
                    :option-list="REQUEST_STATUS_OPTION_LIST"
                    v-model="provideStatus"
                />
            </div>
        </div>
    </SearchForm>
</template>

<script setup lang="ts">
import SearchForm from '@/components/common/layout/SearchForm.vue';
import BaseSelect from '@/components/common/input/BaseSelect.vue';
import DateRangePicker from '@/components/common/input/DateRangePicker.vue';
import {useFieldModelCustom} from '@/hooks/vee-validate/useFieldModelCustom';
import {
    PeriodTypeE,
    PeriodTypeT,
    RequestStatusE,
    RequestStatusKorE,
    RequestStatusT,
    ServiceTypeE,
    ServiceTypeT,
} from '@/types/points/point-keyword-types';
import {PointRequestHistorySearchFormStateI} from '@/types/points/point-request-history-types';
import {useField, useFormValues} from 'vee-validate';

// CONST, DOM
const PERIOD_TYPE_OPTION_LIST = [
    {text: '등록일', value: PeriodTypeE.REGISTER_DATE},
    {text: '지급예정일', value: PeriodTypeE.RESERVE_DATE},
];

const SERVICE_TYPE_OPTION_LIST = [
    {text: '전체', value: 'ALL'},
    {text: '즉시지급', value: ServiceTypeE.IMMEDIATELY},
    {text: '예약지급', value: ServiceTypeE.RESERVE},
    {text: '회수', value: ServiceTypeE.RETRIEVE},
];

const REQUEST_STATUS_OPTION_LIST = [
    {text: '전체', value: 'ALL'},
    {text: RequestStatusKorE.요청대기, value: RequestStatusE.REQUEST_READY},
    {text: RequestStatusKorE.요청취소, value: RequestStatusE.REQUEST_CANCEL},
    {text: RequestStatusKorE.승인, value: RequestStatusE.CONFIRM},
    {text: RequestStatusKorE.반려, value: RequestStatusE.REJECT},
    {text: RequestStatusKorE.처리중, value: RequestStatusE.PROCESSING},
    {text: RequestStatusKorE.처리완료, value: RequestStatusE.FINISHED},
];

const shortcuts: Record<string, [number, number]> = {
    오늘: [Date.now(), Date.now()],
    일주일: [new Date().setDate(new Date().getDate() - 7), Date.now()],
    '1개월': [new Date().setMonth(new Date().getMonth() - 1), Date.now()],
    '3개월': [new Date().setMonth(new Date().getMonth() - 3), Date.now()],
    '6개월': [new Date().setMonth(new Date().getMonth() - 6), Date.now()],
};

// props, emits, libs
const emits = defineEmits<{
    (e: 'submit'): void;
    (e: 'initForm'): void;
}>();

// Form state 참조 & 업데이트용
const {setValue: setFromDateValue} = useField<string>('fromDate');
const {setValue: setToDateValue} = useField<string>('toDate');

const formState = useFormValues<PointRequestHistorySearchFormStateI>();

const {periodType, provideStatus, provideType} = useFieldModelCustom<{
    periodType: PeriodTypeT;
    provideStatus: RequestStatusT | 'ALL';
    provideType: ServiceTypeT | 'ALL';
}>(['periodType', 'provideStatus', 'provideType']);

// methods
const updateFromDateValue = (date: string) => setFromDateValue(date);
const updateToDateValue = (date: string) => setToDateValue(date);

const initForm = () => {
    emits('initForm');
};

const searchHandler = () => {
    emits('submit');
};
</script>

<style lang="scss" scoped>
.form-input-group {
    .title {
        &.date {
            min-width: 55px;
        }
    }

    @media (max-width: 1241px) {
        .title {
            &.status {
                min-width: 55px;
            }
        }
    }

    @media (max-width: 878px) {
        flex: 0 47%;

        .title {
            &.type,
            &.date {
                min-width: 83px;
            }
        }
    }

    @media (max-width: 693px) {
        flex: 0 100%;

        .title {
            &.status {
                min-width: 83px;
            }
        }
    }
}
</style>
